import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, FolderOpen, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    readProspectListContext,
    clearProspectListContext,
    saveReturnState,
} from '@/hooks/useReturnNavigation';

const CURSOR_KEY = 'valnexo_prospect_list_cursor';

/**
 * Barre sticky "Dossier suivant" — apparaît uniquement quand l'utilisateur
 * arrive sur une fiche depuis une liste (Dashboard "À closer / à faire avancer",
 * "Dossiers à relancer", Pilotage bucket, page Prospects...).
 *
 * Contenu : ← Précédent · 📁 Nom · X/N · Suivant → · ✕ fermer
 * Raccourcis clavier : ← (prev), → (next), Esc (fermer)
 * Fin de liste : bouton "Suivant" grisé + toast + retour automatique au Dashboard
 */
export default function ProspectNavigationBar({ currentId, prospectName, pipelineHint }) {
    const navigate = useNavigate();
    const params = useParams();
    const activeId = currentId || params.id;
    const [busy, setBusy] = useState(false);
    const [hidden, setHidden] = useState(false);

    // ctx est stable pour toute la vie du sessionStorage entry ; on lit à chaque activeId change
    const ctx = useMemo(() => readProspectListContext(), [activeId]);

    // Cursor persistant : si activeId n'est plus dans la liste (dossier filtré après action),
    // on garde la dernière position connue pour continuer à naviguer.
    const cursorRef = useRef(null);
    useEffect(() => {
        if (!ctx) return;
        const ids = ctx.ids || [];
        const idxLive = ids.indexOf(activeId);
        if (idxLive >= 0) {
            cursorRef.current = idxLive;
            try { sessionStorage.setItem(CURSOR_KEY, String(idxLive)); } catch { /* noop */ }
        } else {
            try {
                const s = sessionStorage.getItem(CURSOR_KEY);
                cursorRef.current = s !== null ? parseInt(s, 10) : 0;
            } catch { cursorRef.current = 0; }
        }
    }, [ctx, activeId]);

    const goTo = useCallback((id) => {
        if (!id || busy) return;
        setBusy(true);
        try {
            saveReturnState(ctx?.source?.path || '/prospects', '', 0);
        } catch { /* noop */ }
        const pipeline = ctx?.source?.pipeline || pipelineHint;
        navigate(`/prospects/${id}${pipeline ? `?module=${pipeline}` : ''}`);
        setTimeout(() => setBusy(false), 400);
    }, [busy, ctx, pipelineHint, navigate]);

    const closeBar = useCallback(() => {
        setHidden(true);
        try { clearProspectListContext(); } catch { /* noop */ }
        try { sessionStorage.removeItem(CURSOR_KEY); } catch { /* noop */ }
    }, []);

    const goBackToSource = useCallback(() => {
        const path = ctx?.source?.path || '/dashboard';
        try { clearProspectListContext(); } catch { /* noop */ }
        try { sessionStorage.removeItem(CURSOR_KEY); } catch { /* noop */ }
        navigate(path);
    }, [ctx, navigate]);

    const handleEndOfList = useCallback(() => {
        toast.success('Fin de la liste — retour au tableau de bord', {
            icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
        });
        setTimeout(() => goBackToSource(), 600);
    }, [goBackToSource]);

    // Calculs (protégés contre ctx null)
    const ids = ctx?.ids || [];
    const idxLive = ids.indexOf(activeId);
    const idx = idxLive >= 0 ? idxLive : (cursorRef.current ?? 0);
    const prevId = idx > 0 ? ids[idx - 1] : null;
    const nextId = idx < ids.length - 1 ? ids[idx + 1] : null;
    const isLast = ids.length > 0 && idx === ids.length - 1;

    // Raccourcis clavier ← → Esc (branchés AVANT le return conditionnel pour respecter les Hooks rules)
    useEffect(() => {
        if (!ctx || hidden) return;
        const onKey = (e) => {
            // Ignore si focus sur input/textarea/contenteditable
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            if (e.key === 'ArrowLeft' && prevId) {
                e.preventDefault();
                goTo(prevId);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (nextId) goTo(nextId);
                else if (isLast) handleEndOfList();
            } else if (e.key === 'Escape') {
                closeBar();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [ctx, hidden, prevId, nextId, isLast, goTo, handleEndOfList, closeBar]);

    if (!ctx || hidden || ids.length === 0) return null;

    const sourceLabel = ctx?.source?.label || 'Retour à la liste';

    return (
        <div
            className="sticky top-0 z-40 -mx-3 sm:-mx-6 lg:-mx-8 mb-3 sm:mb-4 px-3 sm:px-6 lg:px-8 py-2.5 bg-gradient-to-r from-violet-600 via-violet-500 to-purple-600 shadow-md border-b border-violet-700/30"
            data-testid="prospect-navigation-bar"
            role="navigation"
            aria-label="Navigation entre dossiers"
        >
            <div className="max-w-6xl mx-auto flex items-center gap-2 sm:gap-3">
                {/* Précédent */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => prevId && goTo(prevId)}
                    disabled={!prevId || busy}
                    data-testid="prospect-nav-prev-btn"
                    className="h-9 px-2 sm:px-3 text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    aria-label="Dossier précédent (raccourci ←)"
                    title={prevId ? 'Dossier précédent · ←' : 'Premier de la liste'}
                >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1 text-xs font-medium">Précédent</span>
                </Button>

                {/* Nom du dossier + compteur */}
                <div className="flex-1 min-w-0 flex items-center gap-2 justify-center text-white">
                    <FolderOpen className="w-4 h-4 shrink-0 opacity-90" />
                    <span
                        className="truncate text-xs sm:text-sm font-medium"
                        data-testid="prospect-nav-name"
                        title={prospectName || ''}
                    >
                        {prospectName || '…'}
                    </span>
                    <span
                        className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[11px] font-semibold tabular-nums shrink-0"
                        data-testid="prospect-nav-counter"
                        aria-label={`Position ${idx + 1} sur ${ids.length}`}
                    >
                        {idx + 1}/{ids.length}
                    </span>
                </div>

                {/* Suivant (ou "Terminer" si dernier) */}
                {isLast ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEndOfList}
                        data-testid="prospect-nav-finish-btn"
                        className="h-9 px-2 sm:px-3 bg-white/15 text-white hover:bg-white/30 shrink-0"
                        aria-label="Fin de la liste, retour au tableau de bord"
                        title="Fin de la liste"
                    >
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="hidden sm:inline ml-1 text-xs font-medium">Fin</span>
                    </Button>
                ) : (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nextId && goTo(nextId)}
                        disabled={!nextId || busy}
                        data-testid="prospect-nav-next-btn"
                        className="h-9 px-2 sm:px-3 text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                        aria-label="Dossier suivant (raccourci →)"
                        title={nextId ? 'Dossier suivant · →' : 'Dernier de la liste'}
                    >
                        <span className="hidden sm:inline mr-1 text-xs font-medium">Suivant</span>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                )}

                {/* Fermer */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeBar}
                    data-testid="prospect-nav-close-btn"
                    className="h-9 w-9 p-0 text-white/80 hover:text-white hover:bg-white/20 shrink-0"
                    aria-label="Fermer la navigation (raccourci Esc)"
                    title={`Fermer · ${sourceLabel}`}
                >
                    <X className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}
